import { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../config';
import { 
  Plus, Trash2, Edit2, DollarSign, Filter, Calendar, 
  Tag, FileText, X, Save, Search, ChevronDown, ChevronUp,
  Building2, AlertCircle, TrendingUp, TrendingDown
} from 'lucide-react';

// Helper functions for date conversion
const isoToDateBR = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

const dateBRToIso = (dateBR) => {
  if (!dateBR || dateBR.length !== 10) return '';
  const [day, month, year] = dateBR.split('/');
  if (!day || !month || !year) return '';
  return `${year}-${month}-${day}`;
};

const formatDateInput = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const Expenses = () => {
  const { token } = useAdminAuth();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStartDateDisplay, setFilterStartDateDisplay] = useState('');
  const [filterEndDateDisplay, setFilterEndDateDisplay] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    payment_date: '',
    payment_date_display: '',
    category_id: '',
    contract_id: '',
    entry_type: 'expense'
  });

  // Modal type (expense or income)
  const [modalType, setModalType] = useState('expense');

  // Category form
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [categoryError, setCategoryError] = useState('');

  // Error/Success states
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, [filterCategory, filterStartDate, filterEndDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query params
      let params = new URLSearchParams();
      if (filterCategory) params.append('category_id', filterCategory);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);

      const [expensesRes, categoriesRes, contractsRes] = await Promise.all([
        axios.get(`${API}/admin/expenses?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/expense-categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/contracts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setExpenses(expensesRes.data);
      setCategories(categoriesRes.data);
      setContracts(contractsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterStartDateChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setFilterStartDateDisplay(formatted);
    if (formatted.length === 10) {
      setFilterStartDate(dateBRToIso(formatted));
    } else if (formatted.length === 0) {
      setFilterStartDate('');
    }
  };

  const handleFilterEndDateChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setFilterEndDateDisplay(formatted);
    if (formatted.length === 10) {
      setFilterEndDate(dateBRToIso(formatted));
    } else if (formatted.length === 0) {
      setFilterEndDate('');
    }
  };

  const handlePaymentDateChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setFormData(prev => ({
      ...prev,
      payment_date_display: formatted,
      payment_date: formatted.length === 10 ? dateBRToIso(formatted) : ''
    }));
  };

  const openNewExpenseModal = () => {
    setEditingExpense(null);
    setModalType('expense');
    setFormData({
      amount: '',
      description: '',
      payment_date: '',
      payment_date_display: '',
      category_id: '',
      contract_id: '',
      entry_type: 'expense'
    });
    setError('');
    setShowModal(true);
  };

  const openNewIncomeModal = () => {
    setEditingExpense(null);
    setModalType('income');
    setFormData({
      amount: '',
      description: '',
      payment_date: '',
      payment_date_display: '',
      category_id: '',
      contract_id: '',
      entry_type: 'income'
    });
    setError('');
    setShowModal(true);
  };

  const openEditExpenseModal = (expense) => {
    setEditingExpense(expense);
    setModalType(expense.entry_type || 'expense');
    setFormData({
      amount: expense.amount.toString(),
      description: expense.description,
      payment_date: expense.payment_date,
      payment_date_display: isoToDateBR(expense.payment_date),
      category_id: expense.category_id || '',
      contract_id: expense.contract_id || '',
      entry_type: expense.entry_type || 'expense'
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation - category required only for expenses
    if (!formData.amount || !formData.description || !formData.payment_date) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    if (formData.entry_type === 'expense' && !formData.category_id) {
      setError('Categoria é obrigatória para despesas');
      return;
    }

    const amount = parseFloat(formData.amount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      setError('Valor inválido');
      return;
    }

    try {
      const payload = {
        amount: amount,
        description: formData.description,
        payment_date: formData.payment_date,
        category_id: formData.category_id || null,
        contract_id: formData.contract_id || null,
        entry_type: formData.entry_type
      };

      if (editingExpense) {
        await axios.put(`${API}/admin/expenses/${editingExpense.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess(formData.entry_type === 'income' ? 'Receita atualizada com sucesso!' : 'Despesa atualizada com sucesso!');
      } else {
        await axios.post(`${API}/admin/expenses`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess(formData.entry_type === 'income' ? 'Receita criada com sucesso!' : 'Despesa criada com sucesso!');
      }

      setShowModal(false);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar despesa');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta despesa?')) return;

    try {
      await axios.delete(`${API}/admin/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Despesa excluída com sucesso!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao excluir despesa');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setCategoryError('');

    if (!newCategoryName.trim()) {
      setCategoryError('Nome da categoria é obrigatório');
      return;
    }

    try {
      await axios.post(`${API}/admin/expense-categories`, {
        name: newCategoryName,
        description: newCategoryDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewCategoryName('');
      setNewCategoryDescription('');
      setShowCategoryModal(false);
      fetchData();
      setSuccess('Categoria criada com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setCategoryError(err.response?.data?.detail || 'Erro ao criar categoria');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return;

    try {
      await axios.delete(`${API}/admin/expense-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      setSuccess('Categoria excluída com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao excluir categoria');
      setTimeout(() => setError(''), 5000);
    }
  };

  const clearFilters = () => {
    setFilterCategory('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterStartDateDisplay('');
    setFilterEndDateDisplay('');
  };

  // Calculate totals (separate expenses and income)
  const totalExpenses = expenses
    .filter(exp => (exp.entry_type || 'expense') === 'expense')
    .reduce((sum, exp) => sum + exp.amount, 0);
  
  const totalIncome = expenses
    .filter(exp => exp.entry_type === 'income' || exp.entry_type === 'contract_income')
    .reduce((sum, exp) => sum + exp.amount, 0);

  // Group expenses by month
  const expensesByMonth = expenses.reduce((acc, expense) => {
    const date = expense.payment_date;
    if (!date) return acc;
    
    const [year, month] = date.split('-');
    const monthKey = `${year}-${month}`;
    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const monthName = `${monthNames[parseInt(month) - 1]} de ${year}`;
    
    if (!acc[monthKey]) {
      acc[monthKey] = {
        key: monthKey,
        name: monthName,
        entries: [],
        totalExpenses: 0,
        totalIncome: 0
      };
    }
    acc[monthKey].entries.push(expense);
    
    if ((expense.entry_type || 'expense') === 'expense') {
      acc[monthKey].totalExpenses += expense.amount;
    } else {
      acc[monthKey].totalIncome += expense.amount;
    }
    
    return acc;
  }, {});

  // Sort months in descending order (most recent first)
  const sortedMonths = Object.values(expensesByMonth).sort((a, b) => b.key.localeCompare(a.key));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Despesas e Receitas</h1>
          <p className="text-gray-600 text-sm md:text-base">Gerencie despesas e receitas avulsas para conciliação</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCategoryModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            data-testid="manage-categories-btn"
          >
            <Tag size={18} />
            <span className="hidden sm:inline">Categorias</span>
          </button>
          <button
            onClick={openNewIncomeModal}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            data-testid="new-income-btn"
          >
            <TrendingUp size={18} />
            <span>Nova Receita</span>
          </button>
          <button
            onClick={openNewExpenseModal}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            data-testid="new-expense-btn"
          >
            <TrendingDown size={18} />
            <span>Nova Despesa</span>
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md" data-testid="success-message">
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md" data-testid="error-message">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-gray-700 font-medium w-full justify-between"
          data-testid="toggle-filters-btn"
        >
          <div className="flex items-center gap-2">
            <Filter size={18} />
            <span>Filtros</span>
          </div>
          {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {showFilters && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                data-testid="filter-category"
              >
                <option value="">Todas</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
              <input
                type="text"
                value={filterStartDateDisplay}
                onChange={handleFilterStartDateChange}
                placeholder="DD/MM/AAAA"
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
              <input
                type="text"
                value={filterEndDateDisplay}
                onChange={handleFilterEndDateChange}
                placeholder="DD/MM/AAAA"
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                data-testid="filter-end-date"
              />
            </div>
            <div className="sm:col-span-3">
              <button
                onClick={clearFilters}
                className="text-sm text-arcano-blue hover:underline"
                data-testid="clear-filters-btn"
              >
                Limpar filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md p-4 text-white">
          <div className="flex items-center gap-3">
            <TrendingDown size={28} />
            <div>
              <p className="text-red-100 text-xs">Total Despesas</p>
              <p className="text-xl font-bold" data-testid="total-expenses">{formatCurrency(totalExpenses)}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-md p-4 text-white">
          <div className="flex items-center gap-3">
            <TrendingUp size={28} />
            <div>
              <p className="text-green-100 text-xs">Total Receitas</p>
              <p className="text-xl font-bold" data-testid="total-income">{formatCurrency(totalIncome)}</p>
            </div>
          </div>
        </div>
        <div className={`rounded-lg shadow-md p-4 text-white ${(totalIncome - totalExpenses) >= 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' : 'bg-gradient-to-r from-orange-500 to-orange-600'}`}>
          <div className="flex items-center gap-3">
            <DollarSign size={28} />
            <div>
              <p className="text-white/80 text-xs">Saldo</p>
              <p className="text-xl font-bold" data-testid="balance">{formatCurrency(totalIncome - totalExpenses)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Expenses List - Grouped by Month */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue"></div>
        </div>
      ) : expenses.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center text-gray-500">
          <DollarSign size={48} className="mx-auto mb-4 text-gray-300" />
          <p>Nenhum lançamento encontrado</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="expenses-by-month">
          {sortedMonths.map((monthData) => (
            <div key={monthData.key} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              {/* Month Header */}
              <div className="bg-gray-800 text-white px-4 py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Calendar size={20} />
                  {monthData.name}
                </h3>
                <div className="flex gap-4 text-sm">
                  <div className="text-right">
                    <p className="text-gray-400 text-xs">Despesas</p>
                    <p className="font-bold text-red-400">{formatCurrency(monthData.totalExpenses)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-400 text-xs">Receitas</p>
                    <p className="font-bold text-green-400">{formatCurrency(monthData.totalIncome)}</p>
                  </div>
                  <div className="text-right border-l border-gray-600 pl-4">
                    <p className="text-gray-400 text-xs">Saldo</p>
                    <p className={`font-bold ${(monthData.totalIncome - monthData.totalExpenses) >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                      {formatCurrency(monthData.totalIncome - monthData.totalExpenses)}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Month Table */}
              <div className="overflow-x-auto">
                <table className="w-full" data-testid={`expenses-table-${monthData.key}`}>
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Dia</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Descrição</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Categoria</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Valor</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {monthData.entries
                      .sort((a, b) => a.payment_date.localeCompare(b.payment_date))
                      .map((entry) => {
                        const isIncome = entry.entry_type === 'income' || entry.entry_type === 'contract_income';
                        const isContractIncome = entry.entry_type === 'contract_income';
                        const isManualEntry = entry.source === 'manual' || !entry.source;
                        return (
                      <tr key={entry.id} className={`hover:bg-gray-50 ${isContractIncome ? 'bg-blue-50/30' : isIncome ? 'bg-green-50/30' : ''}`} data-testid={`expense-row-${entry.id}`}>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {entry.payment_date.split('-')[2]}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            isContractIncome 
                              ? 'bg-blue-100 text-blue-700' 
                              : isIncome 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                          }`}>
                            {isContractIncome ? <FileText size={12} /> : isIncome ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {isContractIncome ? 'Contrato' : isIncome ? 'Receita' : 'Despesa'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="max-w-xs truncate" title={entry.description}>
                            {entry.description}
                          </div>
                          {isContractIncome && entry.client_name && (
                            <div className="text-xs text-gray-500 truncate">
                              {entry.client_name}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            isContractIncome ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-700'
                          }`}>
                            <Tag size={12} />
                            {entry.category_name}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-semibold whitespace-nowrap ${
                          isContractIncome ? 'text-blue-600' : isIncome ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {isIncome || isContractIncome ? '+' : '-'} {formatCurrency(entry.amount)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isManualEntry ? (
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => openEditExpenseModal(entry)}
                              className="p-2 text-gray-500 hover:text-arcano-blue hover:bg-blue-50 rounded-md transition-colors"
                              title="Editar"
                              data-testid={`edit-expense-${entry.id}`}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Excluir"
                              data-testid={`delete-expense-${entry.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Automático</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New/Edit Expense Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="expense-modal">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl font-bold ${modalType === 'income' ? 'text-green-700' : 'text-gray-900'}`}>
                  {editingExpense 
                    ? (modalType === 'income' ? 'Editar Receita' : 'Editar Despesa')
                    : (modalType === 'income' ? 'Nova Receita' : 'Nova Despesa')
                  }
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Type indicator */}
              <div className={`mb-4 px-3 py-2 rounded-md text-sm ${modalType === 'income' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {modalType === 'income' 
                  ? 'Receitas são usadas para conciliação de caixa e entram no fluxo de caixa como entradas.'
                  : 'Despesas são saídas de caixa categorizadas para controle financeiro.'
                }
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center gap-2">
                  <AlertCircle size={18} />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                    <input
                      type="text"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0,00"
                      className={`w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:border-transparent ${modalType === 'income' ? 'border-green-300 focus:ring-green-500' : 'border-gray-300 focus:ring-arcano-blue'}`}
                      data-testid="expense-amount"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={modalType === 'income' ? "Descreva a receita..." : "Descreva a despesa..."}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="expense-description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Calendar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData.payment_date_display}
                      onChange={handlePaymentDateChange}
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                      data-testid="expense-date"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">A data deve ser no passado</p>
                </div>

                {/* Category - only required for expenses */}
                {modalType === 'expense' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="expense-category"
                  >
                    <option value="">Selecione uma categoria</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                )}

                {/* Contract - only for expenses */}
                {modalType === 'expense' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contrato Vinculado <span className="text-gray-400">(opcional)</span>
                  </label>
                  <select
                    value={formData.contract_id}
                    onChange={(e) => setFormData({ ...formData, contract_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="expense-contract"
                  >
                    <option value="">Nenhum</option>
                    {contracts.map(contract => (
                      <option key={contract.id} value={contract.id}>
                        {contract.description || contract.client_email} - {formatCurrency(contract.total_value)}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Útil para &quot;Custo de Produto&quot; vinculado a um projeto específico</p>
                </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className={`flex items-center gap-2 px-4 py-2 text-white rounded-md transition-colors ${modalType === 'income' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                    data-testid="save-expense-btn"
                  >
                    <Save size={18} />
                    {editingExpense ? 'Atualizar' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Categories Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="categories-modal">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Categorias de Despesas</h2>
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <X size={20} />
                </button>
              </div>

              {/* New Category Form */}
              <form onSubmit={handleCreateCategory} className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Nova Categoria</h3>
                {categoryError && (
                  <div className="mb-3 text-sm text-red-600">{categoryError}</div>
                )}
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Nome da categoria"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="new-category-name"
                  />
                  <input
                    type="text"
                    value={newCategoryDescription}
                    onChange={(e) => setNewCategoryDescription(e.target.value)}
                    placeholder="Descrição (opcional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="new-category-description"
                  />
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-arcano-blue text-white rounded-md hover:bg-blue-700 transition-colors"
                    data-testid="create-category-btn"
                  >
                    <Plus size={18} />
                    Criar Categoria
                  </button>
                </div>
              </form>

              {/* Categories List */}
              <div className="space-y-2">
                <h3 className="font-medium text-gray-900 mb-3">Categorias Existentes</h3>
                {categories.map(cat => (
                  <div 
                    key={cat.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    data-testid={`category-item-${cat.id}`}
                  >
                    <div>
                      <p className="font-medium text-gray-900">{cat.name}</p>
                      {cat.description && (
                        <p className="text-sm text-gray-500">{cat.description}</p>
                      )}
                      {cat.is_default && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 mt-1">
                          Padrão
                        </span>
                      )}
                    </div>
                    {!cat.is_default && (
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Excluir categoria"
                        data-testid={`delete-category-${cat.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
